import React from 'react';
import { FormattedMessage } from 'react-intl';
import { get } from 'lodash';

import { Battery } from '../atoms/icons/battery';
import { SolarPanel } from '../atoms/icons/solar-panel';
import { ParentPaper } from '../atoms/parent-paper';
import { SliderBox } from '../molecules/slider-box';

import { ChartBox } from '../molecules/chart-box';
import { SwitchBox } from '../molecules/switch-box';
import { ValueBox } from '../molecules/value-box';
import { Group } from '../organisms/group';
import { useSelector } from '../store/utils';
import { Typography } from '@mui/material';
import { FormattedValue } from '../store/formatted-value';
import { FormattedStatus } from '../store/formatted-status';
import { Schedules } from '../organisms/schedules/schedules';
import { batteryScheduleTypes } from '../organisms/schedules/schedules.types';
import { useEmsInstalled } from '../utils/ems';
import { FormattedBessStatus } from '../atoms/formatted-bess-status';
import { Inverter } from '../shared';

const Photovoltaics = () => {
  const [
    premium,
    stringVisibility,
    string3,
    string4,
    string5,
    string6,
    wbBatteryModeActive,
  ] = useSelector(
    (s) =>
      [
        get(s.variables, 'PREMIUM'),
        get(s.variables, 'PV_ACTUAL_POWER_STRING_V'),
        get(s.variables, 'PV_STRING_NUMBER') > 2,
        get(s.variables, 'PV_STRING_NUMBER') > 3,
        get(s.variables, 'PV_STRING_NUMBER') > 4,
        get(s.variables, 'PV_STRING_NUMBER') > 5,
        Boolean(
          get(s.variables, 'WB_MODE_PV') || get(s.variables, 'WB_MODE_PV_FAST')
        ),
      ] as const
  );

  const emsInstall = useEmsInstalled();
  const desBatteryContainer = useSelector(({ variables }) =>
    Object.values(variables.INVERTER_CONFIG ?? {}).some(
      (config) => config.type === Inverter.DesBatteryContainer
    )
  );

  return (
    <>
      <Group layout="list">
        <SwitchBox
          title={
            <FormattedMessage
              id="photovoltaics.status"
              defaultMessage="Režim FVE"
            />
          }
          subtitle={<FormattedStatus type="photovoltaic" />}
          fullWidth
        />
        {desBatteryContainer && (
          <SwitchBox
            title={
              <FormattedMessage
                id="photovoltaics.batteryStatus"
                defaultMessage="Stav BMS"
              />
            }
            subtitle={<FormattedBessStatus />}
            fullWidth
          />
        )}
      </Group>

      <Group
        layout="list"
        title={
          <FormattedMessage
            id="photovoltaics.statistics"
            defaultMessage="Výkon elektrárny"
          />
        }
      >
        <ChartBox
          variableId="PV_POWER_GRAPH"
          groupSubStatsByInverter={emsInstall}
        />
      </Group>
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="photovoltaics.statisticsSoc"
            defaultMessage="Úroveň nabití baterie"
          />
        }
        visibility={{ variableId: 'PV_BATTERY' }}
      >
        <ChartBox variableId="PV_BATTERY_SOC_GRAPH" />
      </Group>
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="photovoltaics.statistics.charge"
            defaultMessage="Nabíjení baterie"
          />
        }
        visibility={{ variableIds: ['PV_BATTERY', 'PREMIUM'] }}
        plus
      >
        <ChartBox variableId="PV_BATTERY_CHARGE_GRAPH" />
      </Group>
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="photovoltaics.statistics.discharge"
            defaultMessage="Vybíjení baterie"
          />
        }
        visibility={{ variableIds: ['PV_BATTERY', 'PREMIUM'] }}
        plus
      >
        <ChartBox variableId="PV_BATTERY_DISCHARGE_GRAPH" />
      </Group>
      <Group
        title={
          <FormattedMessage
            id="photovoltaics.common"
            defaultMessage="Obecné informace"
          />
        }
        layout="list"
      >
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="photovoltaics.common.power"
              defaultMessage="Aktuální výkon"
            />
          }
          value={
            <FormattedValue
              variableId="PV_ACTUAL_POWER"
              opts={{
                style: 'unit',
                unit: 'kilowatt',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              }}
            />
          }
          icon={<SolarPanel />}
          expanded={
            stringVisibility ? (
              <Typography
                variant="body1"
                sx={{ color: 'text.disabled', mt: 1 }}
              >
                <FormattedMessage
                  id="photovoltaics.common.string1"
                  defaultMessage="String 1:"
                />
                &nbsp;
                <FormattedValue
                  variableId="PV_ACTUAL_POWER_STRING[0]"
                  opts={{
                    style: 'unit',
                    unit: 'kilowatt',
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                  }}
                />
                <br />
                <FormattedMessage
                  id="photovoltaics.common.string2"
                  defaultMessage="String 2:"
                />
                &nbsp;
                <FormattedValue
                  variableId="PV_ACTUAL_POWER_STRING[1]"
                  opts={{
                    style: 'unit',
                    unit: 'kilowatt',
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                  }}
                />
                {string3 && (
                  <>
                    <br />
                    <FormattedMessage
                      id="photovoltaics.common.string3"
                      defaultMessage="String 3:"
                    />
                    &nbsp;
                    <FormattedValue
                      variableId="PV_ACTUAL_POWER_STRING[2]"
                      opts={{
                        style: 'unit',
                        unit: 'kilowatt',
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      }}
                    />
                  </>
                )}
                {string4 && (
                  <>
                    <br />
                    <FormattedMessage
                      id="photovoltaics.common.string4"
                      defaultMessage="String 4:"
                    />
                    &nbsp;
                    <FormattedValue
                      variableId="PV_ACTUAL_POWER_STRING[3]"
                      opts={{
                        style: 'unit',
                        unit: 'kilowatt',
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      }}
                    />
                  </>
                )}
                {string5 && (
                  <>
                    <br />
                    <FormattedMessage
                      id="photovoltaics.common.string5"
                      defaultMessage="String 5:"
                    />
                    &nbsp;
                    <FormattedValue
                      variableId="PV_ACTUAL_POWER_STRING[4]"
                      opts={{
                        style: 'unit',
                        unit: 'kilowatt',
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      }}
                    />
                  </>
                )}
                {string6 && (
                  <>
                    <br />
                    <FormattedMessage
                      id="photovoltaics.common.string6"
                      defaultMessage="String 6:"
                    />
                    &nbsp;
                    <FormattedValue
                      variableId="PV_ACTUAL_POWER_STRING[5]"
                      opts={{
                        style: 'unit',
                        unit: 'kilowatt',
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      }}
                    />
                  </>
                )}
              </Typography>
            ) : undefined
          }
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="photovoltaics.common.power_total"
              defaultMessage="Celková výroba"
            />
          }
          value={
            <FormattedValue
              variableId="PV_ENERGY_PRODUCED_TOTAL"
              opts={{
                style: 'unit',
                unit: 'kilowatt-hour',
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }}
            />
          }
          icon={<SolarPanel />}
          expanded={
            <Typography variant="body1" sx={{ color: 'text.disabled', mt: 1 }}>
              <FormattedMessage
                id="photovoltaics.common.power_today"
                defaultMessage="Dnešní výroba:"
              />
              &nbsp;
              <FormattedValue
                variableId="STATS_PV_ENERGY_PRODUCED_TODAY"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
              <br />
              <FormattedMessage
                id="photovoltaics.common.power_year"
                defaultMessage="Roční výroba:"
              />
              &nbsp;
              <FormattedValue
                variableId="STATS_PV_ENERGY_PRODUCED_YEAR"
                opts={{
                  style: 'unit',
                  unit: 'kilowatt-hour',
                  maximumFractionDigits: 1,
                  minimumFractionDigits: 1,
                }}
              />
            </Typography>
          }
        />
      </Group>
      <Group
        title={
          <FormattedMessage
            id="photovoltaics.battery"
            defaultMessage="Baterie"
          />
        }
        visibility={{ variableId: 'PV_BATTERY' }}
        layout="list"
      >
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="photovoltaics.battery.battery_charged"
              defaultMessage="Nabití baterie"
            />
          }
          value={
            <FormattedValue
              variableId="PV_ACTUAL_SOC"
              opts={{
                style: 'percent',
                maximumFractionDigits: 0,
              }}
            />
          }
          icon={<Battery />}
          visibility={{ variableId: 'PV_BATTERY' }}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="photovoltaics.battery.battery_power"
              defaultMessage="Výkon baterie"
            />
          }
          value={
            <FormattedValue
              variableId="PV_ACTUAL_POWER_BATTERY"
              opts={{
                style: 'unit',
                unit: 'kilowatt',
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
                signDisplay: 'exceptZero',
              }}
            />
          }
          icon={<Battery />}
          visibility={{ variableId: 'PV_BATTERY' }}
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="photovoltaics.battery.soc_actual"
              defaultMessage="Vypočtené minimální nabití"
            />
          }
          value={
            <FormattedValue
              variableId="PV_SOC_MIN_FINAL"
              opts={{
                style: 'percent',
                maximumFractionDigits: 0,
              }}
            />
          }
          icon={<Battery />}
          visibility={{ variableId: 'PV_BATTERY' }}
        />
        <ParentPaper>
          <SwitchBox
            title={
              <FormattedMessage
                id="photovoltaics.common.battery_manual_charge"
                defaultMessage="Manuálně nabít baterii"
              />
            }
            subtitle={
              <FormattedMessage
                id="photovoltaics.common.battery_manual_charge_subtitle"
                defaultMessage="Manuální nabíjení baterie nabije baterii na cílovou úroveň nabití pokud nedojde k dřívějšímu vypnutí uživatelem."
              />
            }
            switchVariableId="SP_BATTERY_MANUAL_CHARGE"
            visibility={{ variableIds: ['PV_BATTERY', 'PV_BATTERY_CONTROL'] }}
            fullWidth
          />
          <SliderBox
            title={
              <FormattedMessage
                id="photovoltaics.common.battery_manual_charge_soc"
                defaultMessage="Cílové SOC pro manuální nabíjení"
              />
            }
            variableId="SP_BATTERY_MANUAL_CHARGE_SOC"
            opts={{ style: 'percent', maximumFractionDigits: 0 }}
            min={20}
            max={100}
            visibility={{ variableIds: ['PV_BATTERY', 'PV_BATTERY_CONTROL'] }}
          />
        </ParentPaper>
        <SwitchBox
          title={
            <FormattedMessage
              id="photovoltaics.battery.battery_manual_discharge"
              defaultMessage="Manuálně vybít baterii"
            />
          }
          subtitle={
            <FormattedMessage
              id="photovoltaics.common.battery_manual_discharge_subtitle"
              defaultMessage="Manuální vybíjení baterie vybije baterii na minimální úroveň nabití pokud nedojde k dřívějšímu vypnutí uživatelem."
            />
          }
          switchVariableId="SP_BATTERY_MANUAL_DISCHARGE"
          visibility={{ variableIds: ['PV_BATTERY', 'PV_BATTERY_CONTROL'] }}
          fullWidth
        />
      </Group>
      <Group
        title={
          <FormattedMessage
            id="photovoltaics.battery_stop"
            defaultMessage="Blokace baterie"
          />
        }
        visibility={{
          variableIds: [
            'PV_BATTERY',
            'BATTERY_DISCHARGE_BLOCKED_SETTINGS_VISIBLE',
          ],
        }}
        layout="list"
      >
        <SwitchBox
          title={
            <FormattedMessage
              id="photovoltaics.battery_stop.hw"
              defaultMessage="Blokovat baterii při provozu ohřevu vody"
            />
          }
          switchVariableId="PV_BATTERY_STOP_HW"
          visibility={{ variableId: 'HW_INSTALL' }}
          fullWidth
        />
        <SwitchBox
          title={
            <FormattedMessage
              id="photovoltaics.battery_stop.hp"
              defaultMessage="Blokovat baterii při provozu tepelného čerpadla"
            />
          }
          switchVariableId="PV_BATTERY_STOP_HP"
          visibility={{ variableId: 'HP_ENABLED' }}
          fullWidth
        />
        <SwitchBox
          title={
            <FormattedMessage
              id="photovoltaics.battery_stop.wb"
              defaultMessage="Blokovat baterii při provozu wallboxu"
            />
          }
          switchVariableId="PV_BATTERY_STOP_WB"
          visibility={{ variableId: 'WB_INSTALL' }}
          lock={
            wbBatteryModeActive
              ? {
                  when: {},
                  title: (
                    <FormattedMessage
                      id="photovoltaics.battery_stop.wb.lock_title"
                      defaultMessage="Nastavení nelze změnit"
                    />
                  ),
                  description: (
                    <FormattedMessage
                      id="photovoltaics.battery_stop.wb.lock_desc"
                      defaultMessage="Při zapnutém režimu nabíjení wallboxu „Nabíjení podle baterie“ nebo „Přednostní nabíjení podle baterie“ nelze tuto blokaci zapnout. Pro zapnutí blokace nastavte jiný režim nabíjení wallboxu."
                    />
                  ),
                }
              : undefined
          }
          fullWidth
        />
        <SwitchBox
          title={
            <FormattedMessage
              id="photovoltaics.battery_stop.hdo"
              defaultMessage="Vybíjet baterii pouze při nízkém tarifu (HDO)"
            />
          }
          switchVariableId="PV_BATTERY_STOP_HDO"
          fullWidth
        />
      </Group>

      {premium && <Schedules types={batteryScheduleTypes} />}
    </>
  );
};

export default Photovoltaics;
